import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import CopyButton from '@/components/CopyButton';
import styles from './UnconfirmedSaveWords.module.css';

/**
 * D4 (2026-10-18): the read-only presentation of a bound audio row's words
 * whose save the server has not confirmed. Shared by the prayer notice
 * (PrayerDetail's UnconfirmedSaveNotice) and the journal-entry notice
 * (BibleBookmarkSheet's BookmarkUnconfirmedSaveNotice): the title, the body
 * worded for the destination, the words in a blockquote and the existing
 * CopyButton. Nothing else: no Retry, no Remove, no merge, no write. The
 * "already appear" line is a display hint the caller derives from text
 * containment, never deletion authority. Presentation only: the caller's hook
 * selects the row, this component renders it.
 */
interface Props {
  words: string;
  destination: 'prayer' | 'bookmark';
  alreadyPresent: boolean;
}

const BODY_KEYS = {
  prayer: {
    body: 'voice.unconfirmedSaveBody',
    alreadyPresent: 'voice.unconfirmedSaveAlreadyPresent',
  },
  bookmark: {
    body: 'voice.unconfirmedSaveBookmarkBody',
    alreadyPresent: 'voice.unconfirmedSaveBookmarkAlreadyPresent',
  },
} as const;

export default function UnconfirmedSaveWords({ words, destination, alreadyPresent }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const keys = BODY_KEYS[destination];

  return (
    <section
      className={styles.notice}
      role="status"
      aria-live="polite"
      aria-labelledby={titleId}
    >
      <p id={titleId} className={styles.title}>
        {t('voice.unconfirmedSaveTitle')}
      </p>
      <p className={styles.body}>
        {t(alreadyPresent ? keys.alreadyPresent : keys.body)}
      </p>
      <div className={styles.wordsRow}>
        <blockquote className={styles.words}>{words}</blockquote>
        <CopyButton text={words} label={t('voice.unconfirmedSaveCopyAria')} />
      </div>
    </section>
  );
}
